import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { deliveriesClient } from '@/lib/deliveries-client';
import { deliveryProviderDefinition } from '@/lib/delivery-providers-client';

/** The "write my own" entry of the reasons select — Radix rejects an empty string as a value. */
const OTHER = '__other__';

interface CancelDeliveryDialogProps {
  /** Open while set: the branch whose provider supplies the reasons. */
  locationId: string | null;
  /** `order`: the order itself stays as it is; `standalone`: the whole "Solo envío" goes. */
  kind: 'order' | 'standalone';
  /** The provider's `type`: whether it has a reasons list, and the name the copy uses. */
  providerType?: string | null;
  /**
   * Nothing was sent to the provider yet (`externalBookingId: null` — a booking Menteo still holds, or
   * one not created yet): the cancellation stays in Menteo, so the copy does not say "también en…".
   */
  notBookedYet?: boolean;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onDismiss: () => void;
}

/**
 * Cancelling a rider (spec §6): it cancels at the provider too, so it is never one click — and the
 * provider wants a reason, from its own list (`GET /deliveries/cancel-reasons`) when it has one
 * (`supports.cancelReasons`). When there is no list, or it cannot be read, the operator writes one.
 */
export function CancelDeliveryDialog({
  locationId,
  kind,
  providerType,
  notBookedYet = false,
  busy = false,
  onConfirm,
  onDismiss,
}: CancelDeliveryDialogProps) {
  const { t } = useTranslation('orders');
  const provider = deliveryProviderDefinition(providerType);
  const [choice, setChoice] = useState('');
  const [custom, setCustom] = useState('');

  useEffect(() => {
    if (locationId) {
      setChoice('');
      setCustom('');
    }
  }, [locationId]);

  const reasons = useQuery({
    queryKey: ['delivery-cancel-reasons', locationId],
    queryFn: () => deliveriesClient.cancelReasons(locationId!),
    enabled: !!locationId && provider.supports.cancelReasons,
    staleTime: 10 * 60_000,
    retry: false,
  });

  const list = reasons.data ?? [];
  // No list to pick from (failed, or the provider has none): the free-text field is the reason.
  const freeText =
    !provider.supports.cancelReasons || reasons.isError || (reasons.isSuccess && list.length === 0) || choice === OTHER;
  const reason = freeText ? custom.trim() : choice;

  return (
    <AlertDialog open={Boolean(locationId)} onOpenChange={(open) => !open && !busy && onDismiss()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('delivery.cancelDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {kind === 'order'
              ? t(
                  notBookedYet
                    ? 'delivery.cancelDialog.descriptionOrderNotBooked'
                    : 'delivery.cancelDialog.descriptionOrder',
                  { provider: provider.name },
                )
              : t(
                  notBookedYet
                    ? 'delivery.cancelDialog.descriptionStandaloneNotBooked'
                    : 'delivery.cancelDialog.descriptionStandalone',
                  { provider: provider.name },
                )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {reasons.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('delivery.cancelDialog.loadingReasons')}
            </p>
          ) : (
            list.length > 0 && (
              <Field htmlFor="delivery-cancel-reason" label={t('delivery.cancelDialog.reason')} required>
                <Select value={choice} onValueChange={setChoice}>
                  <SelectTrigger id="delivery-cancel-reason" className="w-full">
                    <SelectValue placeholder={t('delivery.cancelDialog.reasonPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {list.map((entry) => (
                      <SelectItem key={entry.value} value={entry.value}>
                        {entry.label}
                      </SelectItem>
                    ))}
                    <SelectItem value={OTHER}>{t('delivery.cancelDialog.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )
          )}
          {reasons.isError && (
            <p role="alert" className="text-xs text-amber-700 dark:text-amber-300">
              {t('delivery.cancelDialog.reasonsFailed', { provider: provider.name })}
            </p>
          )}
          {freeText && !reasons.isLoading && (
            <Field htmlFor="delivery-cancel-custom" label={t('delivery.cancelDialog.customReason')} required>
              <Textarea
                id="delivery-cancel-custom"
                rows={2}
                value={custom}
                onChange={(event) => setCustom(event.target.value)}
                placeholder={t('delivery.cancelDialog.customPlaceholder')}
              />
            </Field>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t('delivery.cancelDialog.keep')}</AlertDialogCancel>
          {/* A plain button, not AlertDialogAction: the dialog stays open while the request runs. */}
          <Button variant="destructive" disabled={!reason || busy} onClick={() => onConfirm(reason)}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('delivery.cancelDialog.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
